import { IDynamicElementService } from '@captiveaire/dynamicelement';
import { TransitionService } from '@uirouter/core';
import * as angular from 'angular';
import { IDialogService } from '../typings/IDialogService';
import { DialogOptions } from '../typings/models';
import { DialogController } from './DialogController';

// legacy component, needs to be kept as the components are migrated to use new one
/**
 * Wrapper for $mdDialog in order to provide defaults for certain types of dialogs
 */
export class DialogService implements IDialogService {
    public static $name: string = 'dialogService';

    public static $inject = [
        '$mdDialog',
        '$transitions',
        'dynamicElementService',
        'lodash',
    ];

    constructor(
        private $mdDialog: angular.material.IDialogService,
        private $transitions: TransitionService,
        private dynamicElementService: IDynamicElementService,
        private lodash: _.LoDashStatic,
    ) {
        // Prevent memory leaks if an open dialog is navigated away from
        this.$transitions.onStart({}, () => {
            this.$mdDialog.cancel();
        });
    }

    public showDialog<TOptions extends DialogOptions>(options: TOptions): angular.IPromise<TOptions> {
        /* New paradigm with angular-material 1.1.4: you should not specify a controller & a view together in order to render a dialog.
         * Instead, we'll always use a controller that properly injects the theme & use directives/components to load controllers/views */

        const dialogOptions = {
            clickOutsideToClose: options.clickOutsideToClose,
            controller: DialogController,
            escapeToClose: this.lodash.isUndefined(options.escapeToClose) ? true : options.escapeToClose,
            locals: {
                data: options.data
            },
            multiple: true,
            targetEvent: options.targetEvent || this.getFakeTargetEvent(),
            template: this.getTemplate(options)
        } as angular.material.IDialogOptions;

        return this.$mdDialog.show(dialogOptions);
    }

    // Dialog that will fill the entire right side of the screen.
    public showDialogView<TOptions extends DialogOptions>(options: TOptions): angular.IPromise<TOptions> {
        const dialogOptions = {
            clickOutsideToClose: options.clickOutsideToClose,
            controller: DialogController,
            fullscreen: true,
            hasBackdrop: false,
            locals: {
                data: options.data
            },
            multiple: true,
            parent: angular.element('#rightViewArea'),
            targetEvent: options.targetEvent || this.getFakeTargetEvent(),
            template: this.getTemplate(options)

        } as angular.material.IDialogOptions;

        return this.$mdDialog.show(dialogOptions);
    }

    private getTemplate(options: DialogOptions) {
        if (options.directInject) {
            return this.dynamicElementService.getTemplate(options.directive, this.getDirectInjectAttributes(options.data));
        }

        return this.dynamicElementService.getTemplate(options.directive, { data: 'data' });
    }

    /**
     * We can direct inject primitives just fine (for example, { digit: 3 } or { word: 'the' } will translate to digit="3" and word="the")
     * However, objects will translate "{ obj: { prop: 'prop' } }" => obj="[object Object]"
     * We still have that object data on the DialogController's data binding, though, so we can just transform the attributes
     */
    private getDirectInjectAttributes(obj: DialogOptions) {
        // Don't mess up the reference, or we will lose that data
        const copy = angular.copy(obj);

        for (const key in copy) {
            if (this.lodash.isObject(copy[key])) {
                copy[key] = `data.${key}`;
            }
        }

        return copy;
    }

    /**
     * The current version of angular-material (1.1.10)
     * requires a targetEvent.target with an md-theme controller in order to render the theme correctly for the dialog
     */
    private getFakeTargetEvent() {
        return {
            target: angular.element('body')
        };
    }
}
